import { Avatar, AvatarGroup, Box, Button, Card, Flex, Grid, Icon, Text, useToast, VStack } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { deleteJob, exportJobsToCsv, exportJobsToXls, fetchJobList } from '../../api/classification/classification'
import api from '../../api/api'
import { ExportIcon, PlusIcon, Trash04Icon } from '../../assets/icons'
import handleToast from '../HandleToast'
import AddJobsModal from '../modals/addJobs'
import DiscardModal from '../modals/discardModal'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useRole } from '../../hooks/auth/useRole'
import { ClassificationJobData } from '../../types/classification/classificationTypes'

interface MemberEntry {
  id: string
  first_name: string
  last_name: string
  profile_picture?: string
  avatar?: string
  current_position?: string
}

interface TableComponentProps {
  data: RowData[]
  setData: React.Dispatch<React.SetStateAction<RowData[]>>
}

const JobTable: React.FC<TableComponentProps> = ({ setData }) => {
  const { t } = useTranslation()
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const itemsPerPage = 15
  const queryClient = useQueryClient()
  const toast = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const [isAddJobsOpen, setIsAddJobsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const { isAdmin, isUserMode } = useRole()

  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const navigate = useNavigate()
  const jobs = useQuery({
    queryKey: ['jobs', page, searchTerm, filters],
    queryFn: () => fetchJobList({ search: searchTerm, page, pageSize: itemsPerPage, filters, fields: 'id,title,description,job_levels,skills.skill.id,skills.skill.name' }),
    staleTime: 30 * 1000,
  })

  const currentJobIds = (jobs?.data?.data || []).map((j: ClassificationJobData) => j.id)
  const { data: memberData } = useQuery({
    queryKey: ['members', page, currentJobIds.join(',')],
    enabled: currentJobIds.length > 0,
    queryFn: async () => {
      const params: Record<string, any> = {
        search: '',
        page: 1,
        page_size: 50,
        fields: 'id,first_name,last_name,profile_picture,avatar,current_position',
        current_position__in: currentJobIds.join(','),
      }
      const res = await api.get('/workforce/', { params })
      return res.data
    },
  })

  const exportJobProfilesToCsv = async () => {
    try {
      setIsExporting(true)

      const selectedIds = selectedRows

      await exportJobsToCsv({
        search: searchTerm,
        filters,
        ids: selectedIds.length > 0 ? selectedIds : undefined,
      })

      handleToast(toast, true, t('classifications.toasts.exportSuccessTitle'), t('classifications.toasts.exportCsvDownloaded'))
    } catch (e) {
      console.error(e)
      handleToast(toast, false, t('classifications.toasts.exportFailedTitle'), t('classifications.toasts.exportFailedMessage'))
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  }

  const exportJobProfilesToXls = async () => {
    try {
      setIsExporting(true)

      const selectedIds = selectedRows

      await exportJobsToXls({
        search: searchTerm,
        filters,
        ids: selectedIds.length > 0 ? selectedIds : undefined,
      })

      handleToast(toast, true, t('classifications.toasts.exportSuccessTitle'), t('classifications.toasts.exportXlsDownloaded'))
    } catch (e) {
      console.error(e)
      handleToast(toast, false, t('classifications.toasts.exportFailedTitle'), t('classifications.toasts.exportFailedMessage'))
    } finally {
      setIsExporting(false)
    }
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.fields.jobTitle'),
      accessor: 'jobTitle',
      type: 'string',
      width: '25%',
      Cell: ({ row }) => (
        <>
          <Text color="solid.900" textStyle="h-xs">
            {row.title}
          </Text>
          <Text color="solid.500" textStyle="b-xs" noOfLines={1} lineHeight="normal" mt="4px">
            {row.description}
          </Text>
        </>
      ),
    },
    {
      label: t('classifications.jobProfiles.table.columns.levels'),
      accessor: 'levels',
      type: 'string',
      Cell: ({ row }) => {
        const capitalizeFirstLetter = (str: string) => {
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        const formatLevels = (levels: string[]) => {
          if (!Array.isArray(levels) || levels.length === 0) return t('common.labels.na');
          return levels
            .map((level) => t(`classifications.levels.${level}`, { defaultValue: capitalizeFirstLetter(level) }))
            .join(', ');
        };

        return (
          <Text textStyle="l-xs" color="solid.900">
            {formatLevels(row.job_levels)}
          </Text>
        );
      },
    },
    {
      label: t('classifications.sections.jobRequirements'),
      accessor: 'skills',
      type: 'string',
      Cell: ({ row }) => {
        if (!row.skills || row.skills.length === 0) {
          return (
            <Text textStyle="l-xs" color="solid.900">
              {t('classifications.messages.noJobRequirementsProvided')}
            </Text>
          );
        }

        const skills = row.skills.map((skill: { skill: { name: string } }) => skill.skill.name);
        const displayedSkills = skills.slice(0, 3).join(', ');
        const remainingCount = skills.length - 3;

        return (
          <Text textStyle="l-xs" color="solid.900" noOfLines={2}>
            {displayedSkills}
            {remainingCount > 0 && (
              <Text as="span" color="solid.900">
                {`, ${t('common.labels.moreCount', { count: remainingCount })}`}
              </Text>
            )}
          </Text>
        );
      },
    },

    {
      label: t('classifications.jobProfiles.table.columns.members'),
      accessor: 'members',
      type: 'string',
      Cell: ({ row }) => {
        const jobMembers: MemberEntry[] = memberData?.data?.filter(
          (member: MemberEntry) => member.current_position === row.id
        ) || []

        if (jobMembers.length === 0) {
          return (
            <Text textStyle="l-xs" color="solid.900">
              {t('classifications.messages.noMembersInList')}
            </Text>
          )
        }

        return (
          <Flex align="center">
            <AvatarGroup size="sm" max={3} spacing="-8px">
              {jobMembers.slice(0, 3).map((member) => (
                <Avatar
                  key={member.id}
                  name={`${member.first_name} ${member.last_name}`}
                  src={member.profile_picture ?? member.avatar}
                  borderWidth="1.5px"
                  borderColor="white"
                />
              ))}
            </AvatarGroup>
            {jobMembers.length > 3 && (
              <Text ml={2} textStyle="l-xs" color="solid.900">
                {t('common.labels.moreCount', { count: jobMembers.length - 3 })}
              </Text>
            )}
          </Flex>
        )
      },
    },

    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm)
  }

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const handleDelete = () => {
    setShowDiscardModal(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => deleteJob(id)))
      setData((prevData) => prevData.filter((row) => !selectedRows.includes(row.id)))
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    } finally {
      setSelectedRows([])
      setShowDiscardModal(false)
    }
  }

  const handleOpenNewTab = (row: RowData) => {
    const path = `/user/classification/jobprofile/${row.id}`
    window.open(path, '_blank')
  }

  const handleRowClick = (row: RowData) => {
    navigate(`/user/classification/jobprofile/${row.id}`)
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prev) => {
        const newIds = jobs.data?.data?.map((row: ClassificationJobData) => row.id)
        return Array.from(new Set([...prev, ...newIds]))
      })
    } else {
      setSelectedRows((prev) => prev.filter((id) => !jobs.data?.data?.some((row: ClassificationJobData) => row.id === id)))
    }
  }

  const handleSelectRow = (id: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prev) => [...prev, id])
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
    }
  }

  const jobData = jobs?.data?.data || []

  React.useEffect(() => {
    if (!jobs.isSuccess) return
    const nextPage = page + 1
    const maxPage = jobs.data?.max_page || 0
    if (!maxPage || nextPage > maxPage) return

    let mounted = true
    ;(async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: ['jobs', nextPage, searchTerm, filters],
          queryFn: () => fetchJobList({
            search: searchTerm,
            page: nextPage,
            pageSize: itemsPerPage,
            filters,
            fields: 'id,title,description,job_levels,skills.skill.id,skills.skill.name',
          }),
          staleTime: 30 * 1000,
        })

        if (!mounted) return
        const cachedNextJobs = queryClient.getQueryData<{ data: ClassificationJobData[] }>(['jobs', nextPage, searchTerm, filters])
        const nextJobIds: string[] = (cachedNextJobs?.data || []).map((j) => j.id).filter(Boolean)
        if (nextJobIds.length === 0) return

        await queryClient.prefetchQuery({
          queryKey: ['members', nextPage, nextJobIds.join(',')],
          queryFn: () => api.get('/workforce/', {
            params: {
              search: '',
              page: 1,
              page_size: 50,
              fields: 'id,first_name,last_name,profile_picture,avatar,current_position',
              current_position__in: nextJobIds.join(','),
            },
          }).then(res => res.data),
          staleTime: 30 * 1000,
        })
      } catch (_err) {
        // no-op
      }
    })()

    return () => { mounted = false }
  }, [jobs.isSuccess, jobs.data?.max_page, page, searchTerm, filters, queryClient])

  const currentPageRowIds = jobData.map((row: ClassificationJobData) => row.id)
  const allSelected = jobs?.data?.data?.length > 0 && currentPageRowIds.every((id) => selectedRows.includes(id as unknown as number))
  const isIndeterminate =
    jobData.length > 0 && currentPageRowIds.some((id) => selectedRows.includes(id as unknown as number)) && !allSelected

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex gap="8px">
              {selectedRows.length > 0 ? (
                <Button  
                  onClick={handleDelete} 
                  variant="error"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon fill="none" color="error.100" mr={2}><Trash04Icon /></Icon>
                  {t('workforce.people.deleteCount', { count: selectedRows.length })}
                </Button>
              ) : (
                <>
                  <FilterButton
                    label={t('classifications.filters.jobLevels')}
                    options={[
                      { id: 'junior', name: t('classifications.levels.junior') },
                      { id: 'medior', name: t('classifications.levels.medior') },
                      { id: 'senior', name: t('classifications.levels.senior') },
                    ]}
                    onApply={(v) => updateFilter('job_levels', v)}
                  />
                  {isAdmin && !isUserMode && (
                    <Button
                      variant="mono"
                      size="sm"
                      h="32px"
                      px="14px"
                      gap="8px"
                      onClick={() => setIsAddJobsOpen(true)}
                    >
                      <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white"><PlusIcon /></Icon>
                      {t('classifications.actions.createNewJobProfile')}
                    </Button>
                  )}
                  <Button
                    variant="white"
                    size="sm"
                    h="32px"
                    px="14px"
                    onClick={handleExportClick}
                  >
                    <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}><ExportIcon /></Icon>
                    {selectedRows.length > 0
                      ? t('workforce.people.exportSelected', { count: selectedRows.length })
                      : t('workforce.people.exportAll')}
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
          {isDropdownOpen && (
                  <Box
                    position="absolute"
                    top="50px"
                    right="0"
                    width="196px"
                    height="92px"
                    borderRadius="8px"
                    mt={2}
                    opacity="1"
                    boxShadow="0px 2px 8px -2px #00000014, 0px 2px 14px -3px #0000001F"
                    backgroundColor="white"
                    zIndex="1000"
                    padding={0}
                  >
                    <VStack align="stretch" spacing={0} height="100%">
                      <Button
                        variant={'dropdown'}
                        height="50%"
                        borderRadius="8px 8px 0 0"
                        onClick={exportJobProfilesToXls}
                      >
                        {t('workforce.people.exportAsXLS')}
                      </Button>
                      <Button variant={'dropdown'} height="50%" borderRadius="0 0 8px 8px" onClick={exportJobProfilesToCsv}>
                        {t('workforce.people.exportAsCSV')}
                      </Button>
                    </VStack>
                  </Box>
                )}          
          {jobs.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={jobData}
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={allSelected}
                onRowClick={handleRowClick}
                onOpenNewTab={handleOpenNewTab}
                isIndeterminate={isIndeterminate}
                tableType="jobs"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={jobs.data?.max_page || 0}
            totalItems={jobs.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
      <DiscardModal
        show={showDiscardModal}
        handleClose={() => setShowDiscardModal(false)}
        handleDelete={handleConfirmDelete}
        action="delete"
        selectedRowsCount={selectedRows.length}
      />
      <AddJobsModal isOpen={isAddJobsOpen} onClose={() => setIsAddJobsOpen(false)} level="" />
    </Card>
  )
}

export default JobTable
